import { useEffect, useRef, useState } from 'react';
import { STAGES } from '@app/constants/stages';
import * as S from './SpeakingRepeatSentence.styles';
import { Recording } from '../Recording';
import { Answer } from '../../Answer';
import { AudioPlayer } from '@app/components/exam/AudioPlayer';
import useCountdownTimer from '@app/hooks/useCountdownTimer';
import VoiceIndicator from '../Recording/VoiceIndicator';
import { useDispatch, useSelector } from 'react-redux';
import { setAllowNext } from '@app/store/slices/mockTestSlice';
import { RootState } from '@app/store/store';
import { useTranslation } from 'react-i18next';
import { use } from 'echarts';

// const gradingContent = 'Please write your name on essay cover sheet .';

interface SpeakingRepeatSentenceProps {
  prepTime: number;
  recordingTime: number;
  audioQuestion: string;
  showAnswer: boolean;
  questionNumber: string;
  isFirstRender: React.MutableRefObject<boolean>;
  gradingContent: string;
  canStart: boolean;
  allowDrag: boolean;
  onAnswerChange?: (audio: Blob | string) => void;
}

export const SpeakingRepeatSentence: React.FC<SpeakingRepeatSentenceProps> = ({
  prepTime,
  recordingTime,
  audioQuestion,
  showAnswer,
  questionNumber,
  gradingContent,
  canStart,
  allowDrag,
  onAnswerChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isCompleteAudio } = useSelector((state: RootState) => state.mockTest);

  const recordingRef = useRef<{ startRecording: () => void; stopRecording: () => void }>(null);
  const [stage, setStage] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [audioURL, setAudioURL] = useState('');
  const [audioBlod, setAudioBlod] = useState<Blob>();
  const [audioDuration, setAudioDuration] = useState<number | null>(null);
  const [timerStage, setTimerStage] = useState<'WAITING' | 'PLAYING'>('WAITING');

  const { seconds, start, reset, finish } = useCountdownTimer({
    initialSeconds: timerStage === 'WAITING' ? prepTime : audioDuration ?? 0,
    onComplete: () => {
      if (timerStage === 'WAITING') {
        setTimerStage('PLAYING');
        setStage(STAGES.PLAYING);
      } else {
        dispatch(setAllowNext(true));
        setStage(STAGES.RECORDING);
        setIsPlaying(false);
        recordingRef.current?.startRecording();
      }
    },
  });

  useEffect(() => {
    if (stage === STAGES.PLAYING && timerStage === 'PLAYING') {
      setIsPlaying(true);
      reset();
      start();
    }
  }, [timerStage, stage]);

  useEffect(() => {
    initialize();
  }, [questionNumber]);

  useEffect(() => {
    if (!showAnswer) return;
    if (stage == STAGES.RECORDING) {
      handleStopRecording();
    } else {
      setStage(STAGES.COMPLETE);
    }
  }, [showAnswer]);

  useEffect(() => {
    if (audioDuration !== null) {
      initialize();
    }
  }, [audioDuration]);

  useEffect(() => {
    if (!isCompleteAudio) return;
    handleStopRecording();
  }, [isCompleteAudio]);

  const initialize = () => {
    setIsPlaying(false);
    setStage(null);
    reset();

    setStage(STAGES.WAITING);
    start();
    // setIsPlaying(true);
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  const handleComplete = async (audio: string) => {
    setStage(STAGES.COMPLETE);
    setAudioURL(audio);

    const blob = await fetch(audio).then((res) => res.blob());
    setAudioBlod(blob);
    onAnswerChange && onAnswerChange(blob);
  };

  const handleStopRecording = () => {
    recordingRef.current?.stopRecording();
  };

  const handleAudioDuration = (duration: number) => {
    const roundedDuration = Math.round(duration);
    setAudioDuration(roundedDuration + 1);
  };

  const handleStart = () => {
    if (stage === STAGES.RECORDING) {
      handleStopRecording();
      return;
    }

    finish();
    setStage(STAGES.RECORDING);
    setIsPlaying(false);
    recordingRef.current?.startRecording();
    onAnswerChange && onAnswerChange('');
  };

  return (
    <>
      <S.Wrapper padding="1rem" marginB="2rem" bg="#e9e9e9">
        <AudioPlayer
          audioQuestion={audioQuestion}
          onEnded={handleEnded}
          startPlaying={isPlaying}
          waitingTime={STAGES.WAITING === stage}
          onDurationChange={handleAudioDuration}
          allowDrag={allowDrag}
          canStart={canStart}
        />
      </S.Wrapper>

      <S.Wrapper>
        <div className="flex">
          <S.Box>
            <p className="font-bold mb-2">{t('page.mockTest.audioRecorder')}</p>
            <S.TextRed>
              {(() => {
                if (seconds && seconds > 0) {
                  if (stage === STAGES.WAITING) return `${t('page.mockTest.beginningIn')} ${seconds}`;
                  else if (stage === STAGES.PLAYING) return `${t('page.mockTest.recordingIn')} ${seconds}`;
                }
                if (stage === STAGES.RECORDING) return t('page.mockTest.button.recording');
                return null;
              })()}
            </S.TextRed>
          </S.Box>
          <S.VoiceIndicator>{stage === STAGES.RECORDING && <VoiceIndicator isRecording={true} />}</S.VoiceIndicator>
        </div>
        <Recording
          ref={recordingRef}
          recordingTime={recordingTime}
          onComplete={handleComplete}
          onStartRecording={handleStart}
          canStart={canStart}
        />
      </S.Wrapper>

      <div className="mt-5">
        {showAnswer && (
          <Answer
            showAnswer={showAnswer}
            audioURL={audioURL}
            showNote={true}
            gradingContent={gradingContent}
            audioQuestion={audioQuestion}
            questionId={questionNumber}
            audioBlod={audioBlod}
          />
        )}
      </div>
    </>
  );
};
